import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, money } from "@/lib/api";

const AUTO = "auto";
const REPARTIR = "repartir";
const fr = (v) => (v === null || v === undefined ? "" : String(v).replace(".", ","));
const options = (annee) => [annee - 1, annee].flatMap((y) => [
  ...[1, 2, 3, 4].map((q) => [`${y}-T${q}`, `T${q} ${y}`]), [`${y}-A`, `Acompte ${y}`], [String(y), `Solde de la déclaration annuelle ${y}`], [`${y}-P`, `Intérêts et pénalités ${y}`],
]);

const Ligne = ({ lib, v, testId, fort }) => (
  <div className={`flex justify-between gap-4 py-1 text-sm ${fort ? "border-t border-slate-200 font-semibold" : ""}`} data-testid={testId}>
    <span className="text-slate-600">{lib}</span><span className="tabular whitespace-nowrap">{v}</span>
  </div>
);

// TPS et TVQ établies sur les avis de cotisation (vide = calcul de l'application pour cette taxe)
const lire = (v) => (v.trim() ? Number(v.replace(/\s/g, "").replace(",", ".")) : null);
const Declare = ({ annee, r, onSaved }) => {
  const [tps, setTps] = useState(fr(r.declare_tps));
  const [tvq, setTvq] = useState(fr(r.declare_tvq));
  useEffect(() => { setTps(fr(r.declare_tps)); setTvq(fr(r.declare_tvq)); }, [r.declare_tps, r.declare_tvq]);
  const enregistrer = async () => {
    const m = { tps: lire(tps), tvq: lire(tvq) };
    if ([m.tps, m.tvq].some((x) => x !== null && (Number.isNaN(x) || x < 0))) { toast.error("Montant invalide"); return; }
    try { await api.put("/conciliation-taxes/declare", { annee: Number(annee), ...m }); toast.success("Montants de l'avis enregistrés"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-600">Taxe nette établie sur les avis de cotisation {annee}</p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500">TPS</span>
        <Input value={tps} onChange={(e) => setTps(e.target.value)} inputMode="decimal" placeholder="avis" className="h-8 w-32 tabular placeholder:text-slate-300" data-testid="conciliation-declare-tps" />
        <span className="text-xs text-slate-500">TVQ</span>
        <Input value={tvq} onChange={(e) => setTvq(e.target.value)} inputMode="decimal" placeholder="avis" className="h-8 w-32 tabular placeholder:text-slate-300" data-testid="conciliation-declare-tvq" />
        <Button size="sm" variant="outline" onClick={enregistrer} data-testid="conciliation-declare-save"><Save className="h-3.5 w-3.5 mr-1" />Enregistrer</Button>
      </div>
      {r.ancien_total && <p className="text-xs text-amber-700" data-testid="conciliation-declare-ancien">Total TPS + TVQ saisi auparavant : {money(r.declare)}. Entrez la TPS et la TVQ séparément pour le remplacer.</p>}
    </div>
  );
};

const ParTaxe = ({ r }) => ["tps", "tvq"].filter((t) => r[`declare_${t}`] !== null).map((t) => (
  <Ligne key={t} lib={`${t.toUpperCase()} établie ${money(r[`declare_${t}`])} − calculée ${money(r[`net_${t}`])}`} v={money(r[`ecart_${t}`])} testId={`bilan-ecart-${t}`} />
));

export const BilanAnnuel = ({ d, onChange }) => {
  const r = d.annuel;
  const du = r.declare ?? r.net_calcule;
  return (
    <div className="mt-5 grid gap-5 md:grid-cols-2" data-testid="conciliation-bilan">
      <div>
        <Declare annee={d.annee} r={r} onSaved={onChange} />
        <div className="mt-3">
          <Ligne lib="Net calculé par l'application" v={money(r.net_calcule)} testId="bilan-net-calcule" />
          <ParTaxe r={r} />
          {r.declare !== null && <Ligne lib="Montant déclaré total (TPS + TVQ)" v={money(r.declare)} testId="bilan-declare" />}
          {r.declare !== null && (r.declare_tps === null || r.declare_tvq === null) && <Ligne lib="Différence déclaré − calculé" v={money(r.ecart_calcul)} testId="bilan-ecart-calcul" />}
        </div>
      </div>
      <div>
        <Ligne lib="Paiements comptés par trimestre" v={money(r.trimestriels)} testId="bilan-trimestriels" />
        <Ligne lib={`Acomptes ${d.annee}`} v={money(r.acomptes)} testId="bilan-acomptes" />
        {r.declare !== null && <Ligne lib={`Reste à payer au solde (dû ${money(r.du)} − acomptes et versements ${money(r.verse_avant_solde)})`} v={money(Math.max(r.reste_a_payer, 0))} testId="bilan-reste-a-payer" fort />}
        <Ligne lib={`Solde de la déclaration annuelle ${d.annee}`} testId="bilan-solde"
          v={<span className={r.declare !== null && r.solde > 0.01 && r.solde > r.reste_a_payer + 0.01 ? "text-red-600" : ""}>{money(r.solde)}{r.declare !== null && r.solde > 0.01 && r.solde > r.reste_a_payer + 0.01 ? ` — dépasse le reste à payer de ${money(r.solde - Math.max(r.reste_a_payer, 0))}` : ""}</span>} />
        <Ligne lib="Total payé pour l'année" v={money(r.paye)} testId="bilan-paye" fort />
        {r.penalites !== 0 && <Ligne lib={`Intérêts et pénalités ${d.annee} (hors écart)`} v={money(r.penalites)} testId="bilan-penalites" />}
        <Ligne lib={`Écart (payé − ${r.declare !== null ? "déclaré" : "calculé"} ${money(du)})`} testId="bilan-ecart"
          v={<span className={Math.abs(r.ecart) < 0.01 ? "text-emerald-700" : r.ecart > 0 ? "text-sky-700" : "text-red-600"}>{Math.abs(r.ecart) < 0.01 ? "0,00 $ ✓" : `${money(r.ecart)} ${r.ecart > 0 ? "payé en trop" : "reste à payer"}`}</span>} fort />
      </div>
    </div>
  );
};

// Paiement qui couvre plusieurs périodes : un montant par période, total égal au paiement
const Repartir = ({ p, annee, onFini }) => {
  const total = Math.abs(p.montant);
  const [parts, setParts] = useState(p.parts.map((x) => ({ periode: x.periode, montant: fr(Math.abs(x.montant)) })));
  const nb = (v) => Number(String(v).replace(/\s/g, "").replace(",", ".")) || 0;
  const somme = Math.round(parts.reduce((s, x) => s + nb(x.montant), 0) * 100) / 100;
  const maj = (i, champ, v) => setParts(parts.map((x, j) => (j === i ? { ...x, [champ]: v } : x)));
  const enregistrer = async () => {
    try {
      await api.put(`/conciliation-taxes/paiement/${p.id}/repartir`, { parts: parts.map((x) => ({ periode: x.periode, montant: nb(x.montant) })) });
      toast.success("Paiement réparti");
      onFini(true);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-2 space-y-2 rounded-md bg-slate-50 p-3" data-testid={`conciliation-repartir-${p.id}`}>
      {parts.map((x, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <Select value={x.periode} onValueChange={(v) => maj(i, "periode", v)}>
            <SelectTrigger className="h-8 w-60 text-xs" data-testid={`conciliation-repartir-${p.id}-periode-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">{options(annee).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={x.montant} onChange={(e) => maj(i, "montant", e.target.value)} inputMode="decimal" className="h-8 w-28 tabular" data-testid={`conciliation-repartir-${p.id}-montant-${i}`} />
          {parts.length > 1 && <Button size="sm" variant="ghost" onClick={() => setParts(parts.filter((_, j) => j !== i))} data-testid={`conciliation-repartir-${p.id}-retirer-${i}`}>Retirer</Button>}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setParts([...parts, { periode: `${annee}-T1`, montant: fr(Math.max(0, Math.round((total - somme) * 100) / 100)) }])} data-testid={`conciliation-repartir-${p.id}-ajouter`}>+ Période</Button>
        <span className={`text-xs ${Math.abs(somme - total) < 0.01 ? "text-emerald-700" : "text-red-600"}`} data-testid={`conciliation-repartir-${p.id}-somme`}>{money(somme)} sur {money(total)}</span>
        <Button size="sm" onClick={enregistrer} disabled={Math.abs(somme - total) >= 0.01} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`conciliation-repartir-${p.id}-ok`}>Enregistrer</Button>
        <Button size="sm" variant="ghost" onClick={() => onFini(false)} data-testid={`conciliation-repartir-${p.id}-annuler`}>Annuler</Button>
      </div>
    </div>
  );
};

// Paiements « TPS & TVQ » : choix de la période que chacun paie
export const PaiementsTaxes = ({ d, onChange }) => {
  const [envoi, setEnvoi] = useState(null);
  const [edition, setEdition] = useState(null);
  const choisir = async (p, v) => {
    if (v === REPARTIR) { setEdition(p.id); return; }
    setEnvoi(p.id);
    try { await api.put(`/conciliation-taxes/paiement/${p.id}`, { periode: v === AUTO ? null : v }); onChange(); } catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(null); }
  };
  if (!d.paiements.length) return <p className="mt-5 text-sm text-slate-500" data-testid="conciliation-aucun-paiement">Aucun paiement « TPS &amp; TVQ » trouvé pour {d.annee}.</p>;
  return (
    <div className="mt-5" data-testid="conciliation-paiements">
      <p className="text-sm font-medium text-slate-700">Paiements à Revenu Québec — « Compte pour »</p>
      <p className="mb-2 text-xs text-slate-500">Choisissez la période que chaque paiement paie (trimestre, acompte ou solde de la déclaration annuelle). « Automatique » suit la règle ci-dessus.</p>
      <div className="space-y-2">{d.paiements.map((p) => (
        <div key={p.id} className="rounded-md border border-slate-200 px-3 py-2" data-testid={`conciliation-paiement-${p.id}`}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <span className="flex-1 min-w-0 text-xs"><span className="font-mono text-slate-500">{p.date}</span> · {p.description} · <span className="font-mono">{money(p.montant)}</span>
            {p.reparti && <span className="ml-1 text-sky-700">(réparti : {p.parts.map((x) => `${x.libelle} ${money(x.montant)}`).join(" · ")})</span>}
            {p.reparti && edition !== p.id && <button type="button" onClick={() => setEdition(p.id)} className="ml-2 text-emerald-700 hover:underline" data-testid={`conciliation-paiement-${p.id}-modifier`}>Modifier la répartition</button>}
            {!p.reparti && p.periode.slice(0, 4) !== String(d.annee) && <span className="ml-1 text-amber-700">(compté pour {p.libelle})</span>}</span>
          {p.report ? <span className="text-xs text-emerald-700 sm:w-64" data-testid={`conciliation-paiement-${p.id}-report`}>Report — compte pour {p.libelle} (suivi des trop-payés)</span> : envoi === p.id ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
            <Select value={p.reparti ? REPARTIR : p.choisie ? p.periode : AUTO} onValueChange={(v) => choisir(p, v)}>
              <SelectTrigger className="h-8 w-full sm:w-64 text-xs" data-testid={`conciliation-paiement-${p.id}-periode`}><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={AUTO} data-testid={`conciliation-paiement-${p.id}-auto`}>Automatique ({p.choisie ? "règle" : p.libelle})</SelectItem>
                <SelectItem value={REPARTIR} data-testid={`conciliation-paiement-${p.id}-repartir`}>Répartir sur plusieurs périodes…</SelectItem>
                {options(d.annee).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`conciliation-paiement-${p.id}-opt-${v}`}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        {edition === p.id && <Repartir p={p} annee={d.annee} onFini={(ok) => { setEdition(null); if (ok) { onChange(); } }} />}
        </div>
      ))}</div>
    </div>
  );
};
